// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Resilient loader for the header weather forecast. The BFF weather relay is
// flaky, so this retries a failed/empty fetch every 30s for up to 10 attempts
// before surfacing an error state; `refetch` resets the attempt counter and
// tries immediately (wired to a click on the widget).

import { useCallback, useEffect, useRef, useState } from 'react';
import { getHeaderForecast } from '../services/weatherService';
import type { WeatherForecast } from '../types/weather';

export const WEATHER_RETRY_MS = 30_000;
export const WEATHER_MAX_ATTEMPTS = 10;

export type ForecastStatus = 'loading' | 'ready' | 'error';

export interface HeaderForecast {
  forecast: WeatherForecast | null;
  status: ForecastStatus;
  /** Force a fresh attempt cycle (resets the retry counter). */
  refetch: () => void;
}

export interface UseHeaderForecastOptions {
  siteId: string;
  /** Delay between retries (default 30s). */
  retryMs?: number;
  /** Total attempts before the error state (default 10). */
  maxAttempts?: number;
}

export function useHeaderForecast({
  siteId,
  retryMs = WEATHER_RETRY_MS,
  maxAttempts = WEATHER_MAX_ATTEMPTS,
}: UseHeaderForecastOptions): HeaderForecast {
  const [forecast, setForecast] = useState<WeatherForecast | null>(null);
  const [status, setStatus] = useState<ForecastStatus>('loading');
  const triesRef = useRef(0);
  const requestIdRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeRef = useRef(true);

  const load = useCallback(
    function load() {
      const requestId = ++requestIdRef.current;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (!siteId) {
        // The roster is still loading. Stay on the initial loading state so the
        // header does not flash an error before a site id exists.
        setForecast(null);
        setStatus('loading');
        return;
      }
      setStatus('loading');
      // A rejected fetch OR a null forecast (no usable upstream data) both count
      // as a failed attempt and schedule a retry until the cap is hit.
      const retry = () => {
        if (triesRef.current >= maxAttempts - 1) {
          setForecast(null);
          setStatus('error');
          return;
        }
        triesRef.current += 1;
        timerRef.current = setTimeout(load, retryMs);
      };
      getHeaderForecast(siteId)
        .then(f => {
          if (!activeRef.current || requestId !== requestIdRef.current) return;
          if (f) {
            setForecast(f);
            setStatus('ready');
            return;
          }
          retry();
        })
        .catch(() => {
          if (activeRef.current && requestId === requestIdRef.current) retry();
        });
    },
    [siteId, retryMs, maxAttempts]
  );

  const refetch = useCallback(() => {
    triesRef.current = 0;
    load();
  }, [load]);

  useEffect(() => {
    activeRef.current = true;
    triesRef.current = 0;
    load();
    return () => {
      activeRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [load]);

  return { forecast, status, refetch };
}
